/**
 * Derives the checkout form's PAGES from its questions.
 *
 * A page break is a flag on the question that STARTS a page
 * (`pageBreakBefore`), not a separate record — so a break can never dangle, and
 * reordering carries it with its question. Pages are never persisted.
 *
 * Legacy `mode: "oneAtTime"` is reinterpreted here as "one page per question",
 * which is exactly what that mode rendered. That keeps the ~125 live listings
 * still on it working with no data migration, so the widget can ship before any
 * migration runs.
 */
import { isQuestionRenderable } from "./guest-questions";

type PageableQuestion = {
  isPerGuest?: boolean;
  systemRole?: string | null;
  pageBreakBefore?: boolean;
  pageTitle?: string;
};

export type QuestionPage = {
  /** Indices into the SORTED questions array. Only renderable questions. */
  questionIndices: number[];
  /** Heading for this page, from the question that starts it. */
  title?: string;
};

export type BuildQuestionPagesOptions = {
  mode?: "all" | "oneAtTime";
  totalGuests: number;
  collectSystemGuests?: boolean;
  participantsEnabled?: boolean;
};

export function buildQuestionPages(
  sortedQuestions: PageableQuestion[],
  {
    mode = "all",
    totalGuests,
    collectSystemGuests = false,
    participantsEnabled = false,
  }: BuildQuestionPagesOptions,
): QuestionPage[] {
  // Slice into raw pages first, keeping hidden questions, so a page's title
  // survives even when the question carrying it renders nothing.
  const rawPages: { indices: number[]; title?: string }[] = [];

  sortedQuestions.forEach((question, index) => {
    const startsNewPage =
      index === 0 || mode === "oneAtTime" || question.pageBreakBefore === true;

    if (startsNewPage) {
      // The break row owns the heading (index 0 has no break carrier, so it
      // has no heading either — see the matching rule in `renumberQuestions`
      // and the editor, which never offers a title input on question 0).
      const title = index === 0 ? undefined : question.pageTitle || undefined;
      rawPages.push({ indices: [index], title });
    } else {
      rawPages[rawPages.length - 1]!.indices.push(index);
    }
  });

  return rawPages
    .map(({ indices, title }) => ({
      questionIndices: indices.filter((i) =>
        isQuestionRenderable(
          sortedQuestions[i]!,
          totalGuests,
          collectSystemGuests,
          participantsEnabled,
        ),
      ),
      title,
    }))
    .filter((page) => page.questionIndices.length > 0);
}

/** Index of the page holding `questionIndex`, or -1 if it renders on no page. */
export function findPageForQuestion(
  pages: QuestionPage[],
  questionIndex: number,
): number {
  return pages.findIndex((page) => page.questionIndices.includes(questionIndex));
}

// Behavior-after-question values (must match @obby/constants
// BehaviorNextQuestionClick: nextQuestion, endForm, specificQuestion).
const BEHAVIOR_END_FORM = "endForm";
const BEHAVIOR_SPECIFIC_QUESTION = "specificQuestion";

type BranchableQuestion = {
  _id: string;
  name?: string;
  order?: number;
  typeOfInput?: string;
  singleSelectOptions?: { name: string; postOptionSelectedTarget?: string }[];
  behaviorAfterQuestion?: string;
  specificNextClickQuestionAnchor?: string | number;
};

/**
 * The single-select option the buyer most recently chose, scoped to the exact
 * question it came from. A page can hold more than one single-select (once
 * schools can merge oneAtTime pages via `pageBreakBefore` in the editor), so a
 * bare option index would silently jump on the wrong question's target — see
 * `nextPageIndex` below.
 */
export type QuestionSelection = { questionIndex: number; optionIndex: number };

/**
 * Resolves the next page index for the buyer's current page, honouring legacy
 * per-question branching: a per-option `postOptionSelectedTarget`, then
 * `endForm` / `specificQuestion` on any question on the page. Falls back to
 * the next sequential page when nothing branches. Returns `pages.length` as
 * the terminal ("end of form") position.
 *
 * `sortedQuestions` is the FULL sorted question list (not just this page's),
 * since a target can point anywhere in the form.
 */
export function nextPageIndex(
  pages: QuestionPage[],
  sortedQuestions: BranchableQuestion[],
  currentPageIndex: number,
  selection: QuestionSelection | null,
): number {
  const page = pages[currentPageIndex];
  if (!page) return currentPageIndex + 1;

  // Per-option jump wins: it is the more specific instruction. Only honour it
  // for the exact question the buyer answered — `selection` tracks one choice
  // for the whole form, so on a page with two single-selects the other one
  // must not steal this target.
  if (selection && page.questionIndices.includes(selection.questionIndex)) {
    const question = sortedQuestions[selection.questionIndex];
    if (question?.typeOfInput === "singleSelect") {
      const target =
        question.singleSelectOptions?.[selection.optionIndex]
          ?.postOptionSelectedTarget;
      if (target) {
        // Prefer the stable _id; fall back to the legacy `name` match.
        let targetIndex = sortedQuestions.findIndex((q) => q._id === target);
        if (targetIndex === -1) {
          targetIndex = sortedQuestions.findIndex((q) => q.name === target);
        }
        const targetPage = findPageForQuestion(pages, targetIndex);
        if (targetPage > -1) return targetPage;
      }
    }
  }

  for (const questionIndex of page.questionIndices) {
    const question = sortedQuestions[questionIndex];
    if (question?.behaviorAfterQuestion === BEHAVIOR_END_FORM) {
      return pages.length; // Terminal position.
    }
    if (question?.behaviorAfterQuestion === BEHAVIOR_SPECIFIC_QUESTION) {
      const anchor = question.specificNextClickQuestionAnchor;
      if (anchor !== undefined && anchor !== null && anchor !== "") {
        // Prefer _id match; fall back to legacy `order` match (which may be
        // stored as either a number or a numeric string).
        let targetIndex = sortedQuestions.findIndex((q) => q._id === anchor);
        if (targetIndex === -1) {
          targetIndex = sortedQuestions.findIndex(
            (q) => String(q.order) === String(anchor),
          );
        }
        const targetPage = findPageForQuestion(pages, targetIndex);
        if (targetPage > -1) return targetPage;
      }
    }
  }

  return currentPageIndex + 1;
}
